import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IonicModule, IonMenu, Platform} from '@ionic/angular';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import {registerFontAwesomeIcons} from './core/icon-registry';
import {BreakpointObserver, Breakpoints, BreakpointState} from '@angular/cdk/layout';
import {NavigationEnd, Router} from '@angular/router';
import {filter, map, Observable, Subject, takeUntil} from 'rxjs';
import {CommonModule} from '@angular/common';

import {CognitoUserService} from './core/services/cognito-user.service';
import {CatchitNavigationService, DeviceType, NavEntity} from './core/services/catchit-navigation.service';
import {NetworkDetector} from './core/services/network-detector.service';
import {environment} from '../environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    imports: [IonicModule, CommonModule, FontAwesomeModule]
})
export class AppComponent implements OnInit, OnDestroy {
    /** Text for the banner at the top of every page; unset means no banner (see environment.interface.ts). */
    testBanner: string | undefined = environment.testBanner;
    /** The production system's banner is amber and the test system's is blue, so the two are easy to tell apart. */
    testBannerClass = environment.production ? 'test-banner-production' : 'test-banner-test';
    isHandset$: Observable<boolean>;
    whenShowMenu = 'none';
    menuPinned = false;
    currentPage: string = '';
    private destroy$ = new Subject<void>();
    // Highlight cache removed - using direct comparison instead of method calls in template

    @ViewChild(IonMenu) ionMenu: IonMenu;

    private platformsForThisDevice: string[];

    constructor(
        private library: FaIconLibrary,
        public userService: CognitoUserService,
        public navService: CatchitNavigationService,
        private router: Router,
        private breakpointObserver: BreakpointObserver,
        private networkDetector: NetworkDetector,
        private platform: Platform,
        private cdr: ChangeDetectorRef,
    ) {
        // Register all FontAwesome icons used in the application
        registerFontAwesomeIcons(library);
        this.platformsForThisDevice = this.platform.platforms();
    }

    ngOnInit() {
        this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset)
            .pipe(map((result: BreakpointState) => result.matches));

        // Initialize current page and subscribe to router changes
        this.currentPage = this.router.url;

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe((event: NavigationEnd) => {
            this.currentPage = event.url;
            this.cdr.detectChanges(); // Manually trigger change detection after route change
        });

        // allowedAccess() reads the user's role synchronously from CognitoUserService's
        // cached state, which is populated asynchronously after this component's first
        // render. Without this subscription, the Site Menu keeps showing its stale
        // "no role loaded yet" result (e.g. hiding Administration for an actual super admin)
        // until some unrelated click happens to trigger a re-render as a side effect -
        // confirmed live. This re-checks the menu the moment the role actually resolves.
        this.userService.getCurrentUser().pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.cdr.detectChanges();
        });
    }

    getCurrentPage() {
        return this.currentPage;
    }

    containsCurrentPage(entity: NavEntity): boolean {
        if (entity.page === this.currentPage) {
            return true;
        }
        if (entity.container) {
            return this.getMenuItemsForPage(entity.page).some(item => {
                return this.containsCurrentPage(item);
            });
        }
        return false;
    }

    gotoPage(page: string) {
        void this.router.navigateByUrl(page).then();
    }

    /**
     * Gates whether a Site Menu entry is shown at all - this was a hardcoded "return true"
     * stub (confirmed live: volunteer/basic-role accounts saw every admin menu item, and
     * new registrations with no role yet saw a full-looking menu). menu-grid.component.ts
     * (the Home page's grid of cards) already implements this correctly for permission and
     * device-type filtering; this mirrors that same logic here so the side menu matches it.
     */
    allowedAccess(entity: NavEntity): boolean {
        if (!this.showBasedOnDevice(entity)) {
            return false;
        }
        if (entity.admin) {
            return this.userService.isAdminSync() || this.userService.isSuperAdminSync();
        } else if (entity.superAdmin) {
            return this.userService.isSuperAdminSync();
        }
        return true;
    }

    private showBasedOnDevice(entity: NavEntity): boolean {
        if (entity.deviceType === DeviceType.NONE) {
            return true;
        }
        return entity.deviceType === this.navService.getDeviceType(this.platformsForThisDevice);
    }

    canShowMenu(): boolean {
        return !(this.currentPage === '/login'
            || this.currentPage === '/forgotten-password'
            || this.currentPage === '/sign-up'
            || this.currentPage === '/not-registered');
    }

    getMenuItemsForPage(page: string): NavEntity[] {
        return this.navService.getMenuItemsForPage(page);
    }

    setMenuPinned(pinned: boolean) {
        this.menuPinned = !pinned;
        this.whenShowMenu = this.menuPinned ? 'lg' : 'none';
    }

    getFontAwesomeIcon(iconName: string): IconDefinition {
        const icon = (solidIcons as any)[iconName];
        return icon || solidIcons.faSquare;
    }

    // Method removed - using direct property comparison in template instead

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
